<template>
  <div>
    <!--    ACCOUNT DEACTIVATION WARNING FOR CERTAIN USERS -->
    <notch-alert
      v-if="showDeactivationWarning"
      ref="deactivationWarning"
      :title="deactivationText"
      :submit="$t('layout.sdui.deactivation_warning.submit')"
      class="fixed"
      :design="2"
      data-test="deactivation-warning-notch-alert"
      @submit-click="openDeactivationHelpArticle()"
    />

    <notch-alert
      v-if="activeModule !== 'timetable' && !hideGlobalNotification && (!$me || $me.school_id !== 22285)"
      :is-important="true"
      :variant="warningType"
      :design="2"
      class="fixed"
      data-test="global-maintenance-notch-alert"
    >
      <template #description>
        <div class="text-left font-weight-normal float-left pb-2 w-100">
          <span class="pr-1" v-html="$sanitize(globalWarningText)" />
          <br />
          <SLink appearance="b-link"
                 :href="$t('global.status_page')"
                 class="text-warning"
          >
            {{ $t('global.status_page') }}
          </SLink>
          <br />

          <div class="d-flex justify-content-end">
            <b-button
              :class="`text-underline text-${warningType}`"
              class="reset-button font-weight-bolder f-14"
              variant="link"
              @click="setStorage({ hide: true })"
            >
              {{ $t('global.remove_hint') }}
            </b-button>
          </div>
        </div>
      </template>
    </notch-alert>

    <!-- Perform school year change alert --->
    <notch-alert
      v-if="canShowSchoolYearChangeReminder"
      :is-important="true"
      :variant="warningType"
      :title="$t('global.warning.title')"
      :design="2"
      class="fixed"
      data-cy="schoolyear-change-reminder"
      data-test="schoolyear-change-reminder-notch-alert"
    >
      <template #title>
        <div class="d-flex justify-content-between px-3">
          <span class="font-weight-bold fz-base pb-1">
            {{ $t('layout.sdui.schoolyear_change_reminder.title') }}
          </span>

          <b-button
            class="icon mt-n1 f-20 reset-button"
            variant="transparent"
            data-cy="hide-close-button"
            @click="hideSchoolYearChangeReminder"
          >
            <span class="sr-only">{{ $t('global.close') }}</span>
            <sdui-icon class="icon-close-o" />
          </b-button>
        </div>
      </template>

      <template #description>
        <div class="text-left font-weight-normal pt-2 pb-2 px-3">
          <p>{{ $t('layout.sdui.schoolyear_change_reminder.content') }}</p>

          <div class="d-flex flex-row-reverse justify-content-between">
            <b-button
              variant="link"
              class="reset-button font-weight-bolder f-14"
              data-cy="schoolyear-change-reminder-confirm-link"
              @click="navigate('school-year-change-type', { schoolUuid: schoolUuid })"
            >
              {{ $t('layout.sdui.schoolyear_change_reminder.confirm') }}
            </b-button>
          </div>
        </div>
      </template>
    </notch-alert>

    <!--    SWITCH USER MODAL FOR PARENTS -->
    <user-change-modal
      v-if="enableSwitchingChildren || $can('manage-sdui', $me) || enableAccountSwitching"
      :is-visible="modalShow"
      :enable-account-switching="enableAccountSwitching"
      data-test="user-change-modal"
      @hide-modal="toggleModal"
    />

    <!--eslint-disable-next-line vue/no-restricted-html-elements-->
    <b-modal id="pseudoModal"
             ref="pseudoModalRef"
             v-model="modalShow"
             dialog-class="d-none"
             modal-class="switch-children-modal"
    />

    <event-creation-modal-wrapper
      v-model="modals.eventCreation.showModal"
      data-test="event-creation-modal-wrapper"
      :return-focus="modals.eventCreation.returnFocusTo"
      @close="closeEventCreationModal"
    />

    <SConfirmModal
      id="logout-user-modal"
      v-model="isLogoutModalVisible"
      :ok-title="$t('layout.sdui.logout_modal.logout')"
      :cancel-title="$t('layout.sdui.logout_modal.refresh')"
      :no-close-on-backdrop="true"
      :no-close-on-esc="true"
      :title="$t('layout.sdui.logout_modal.title')"
      data-test="logout-user-confirm-modal"
      @hidden="isLogoutModalVisible = false"
      @ok="logoutUser"
      @cancel="refreshPage"
    >
      <template #description>
        <div data-cy="logout-user-modal">{{ $t('layout.sdui.logout_modal.text') }}</div>
      </template>
    </SConfirmModal>
  </div>
</template>
<script>
import UserChangeModal from 'src/layout/UserChangeModal.vue'
import NotchAlert from 'src/components/miscellaneous/ui/notch/NotchAlert.vue'
import globalWarning from 'src/mixins/miscellaneous/globalWarning.js'
import isMobileDeviceMixin from 'src/mixins/miscellaneous/isMobileDeviceMixin.js'
import multiUserMixin from '../mixins/authentification/multiUserMixin.js'
import schoolYearMixin from 'src/mixins/management/schoolYearMixin.js'
import EventCreationModalWrapper from 'src/components/calendar/event/creation/EventCreationModalWrapper.vue'

export default {
  name: 'SduiAlertModals',

  components: {
    EventCreationModalWrapper,
    NotchAlert,
    UserChangeModal,
  },

  mixins: [globalWarning, isMobileDeviceMixin, multiUserMixin, schoolYearMixin],

  data: () => ({
    modalShow: false,
    classbook: null,
    isLogoutModalVisible: false,
    schoolYearChangeReminderHidden: false,
    modals: {
      eventCreation: {
        showModal: false,
        returnFocusTo: null,
        creationQueryParam: 'event-creation',
        otherQueryParams: ['event-creation', 'event-locations', 'event-categories', 'event-participants'],
      },
    },
  }),

  computed: {
    enableSwitchingChildren() {
      return this.hasMultipleAccounts
    },

    enableAccountSwitching() {
      const testingSchools = [import.meta.env.VITE_SLINK_SDUI_GMBH_INTERNAL_SCHOOL, import.meta.env.VITE_SLINK_MUSTERSCHULE_KOBLENZ]
      return testingSchools.includes(this.$me?.school?.slink) || (localStorage && !!localStorage.getItem('sampleSchoolData'))
    },

    activeModule() {
      return this.$route.meta.group // this.$store.state.routerGroup
    },

    showDeactivationWarning() {
      return this.$me && this.$me.expire_at && this.activeModule !== 'timetable'
    },

    deactivationText() {
      if (!this.$me.expire_at) return ''
      return `${this.$t('layout.sdui.deactivation_text.basic_text')} ${this.$t('layout.sdui.deactivation_text.secondary_text', { date: this.$dateFormat.date_yearlong(this.$me.expire_at) })} ${this.$t('layout.sdui.deactivation_text.additional_info')}`
    },

    showSchoolBannerEarly() {
      return this.isEndOfSchoolYear && !!this.upcomingSchoolYear
    },

    canShowSchoolYearChangeReminder() {
      // Handles show/hide banner logic
      const isAfterSchoolYearEnd = this.$moment().isAfter(this.currentSchoolYear?.ends_at)
      const canChangeSchoolYear = this.$can('change-schoolyear', this.$me?.school?.timetable)
      const canShowBanner = this.showSchoolBannerEarly || isAfterSchoolYearEnd
      if (!canShowBanner || !canChangeSchoolYear || this.schoolYearChangeReminderHidden) return false
      const displayAt = localStorage?.getItem('schoolYearChangeReminderHiddenUntil')
      if (!displayAt) return true
      // Handle show banner again logic
      const showHintAgain = this.$moment().isSameOrAfter(displayAt)
      return showHintAgain && isAfterSchoolYearEnd
    },

    schoolUuid() {
      return this.$me?.school.uuid
    },

    queryEventCreation() {
      return this.$route.query[this.modals.eventCreation.creationQueryParam]
    },

    queryEventEdit() {
      return this.queryEventCreation && this.$route.query.id
    },

    showLogoutModal() {
      return this.$store.getters['users/getIsUserInValid']
    },
  },

  methods: {
    async logoutUser() {
      await this.$store.dispatch('users/logoutClearCache', this.$me, { root: true })
    },

    async refreshPage() {
      location.reload()
    },

    toggleModal(hide) {
      hide ? this.$refs.pseudoModalRef.hide() : this.$refs.pseudoModalRef.show()
    },

    navigate(name, params = {}, query = {}) {
      if (this.$route.name === name) return
      this.$router.push({ name: name, params: params, query: query })
    },

    openDeactivationHelpArticle() {
      window.open(this.$t('layout.sdui.deactivation_faq_link'), '_blank')
    },

    hideSchoolYearChangeReminder() {
      this.schoolYearChangeReminderHidden = true
      if (!localStorage) return
      localStorage.setItem('schoolYearChangeReminderHiddenUntil', this.$dateFormat.timestamp(this.$moment().add(1, 'days')))
    },

    openEventCreation() {
      this.modals.eventCreation.returnFocusTo = this.queryEventEdit ? '#edit-event-button' : '#btn-notch'
      this.modals.eventCreation.showModal = true
    },

    async closeEventCreationModal() {
      this.modals.eventCreation.showModal = false

      const queryParams = this.modals.eventCreation.otherQueryParams.reduce((acc, paramName) => {
        acc[paramName] = undefined
        return acc
      }, {})
      await this.$router.replace({ query: { ...this.$route.query, ...queryParams } })
    },
  },

  watch: {
    '$me.school_id': {
      handler: async function (schoolId) {
        // removed absenceCounting check for now
        if (!schoolId) return
        this.classbook = this.$me.school.getCurrentActiveClassbook()
      },

      immediate: true,
    },

    showLogoutModal: {
      handler: function (newVal) {
        if (newVal) {
          this.isLogoutModalVisible = newVal
        }
      },

      immediate: true,
    },

    queryEventCreation: {
      handler: function (eventCreation) {
        // Back button was clicked and the route was already routed back. We still need to reset our boolean var so the modal also closes
        if (!eventCreation && this.modals.eventCreation.showModal) this.modals.eventCreation.showModal = false
        // Open modal if $watch of 'modals.eventCreation.showModal' did a router.push
        if (eventCreation && !this.modals.eventCreation.showModal) this.openEventCreation()
      },

      immediate: true,
    },
  },
}
</script>
